import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Group,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowLeftBar,
  IconCalculator,
  IconInfoCircle,
  IconMathSymbols,
  IconSparkles,
  IconTargetArrow,
} from "@tabler/icons-react";
import { FormEvent, ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../seo/utils";
import GetHelpForm from "../Shared/GetHelpForm";
import "./RacineCarreeCalculatorPage.css";

type CalculatorMode = "square-root" | "square-and-root";

type CalculationResult = {
  inputNumber: number;
  normalizedInput: string;
  mode: CalculatorMode;
  squareValue: string;
  squareRootResult: string;
  approximateResult?: string;
  explanation: string;
  isPerfectSquare: boolean;
  hasImaginaryRoot: boolean;
  simplifiedRadical?: string;
};

type ToolLink = {
  title: string;
  path: string;
  description: string;
};

const TOOL_PATH = "/tools/racine-carree-calculator";
const TOOLS_PATH = "/tools";
const seoTitle = "Racine Carrée Calculator | Free Square Root Calculator";
const seoDescription =
  "Use SubjectBuddy's free racine carrée calculator to calculate square roots instantly and learn simple square root examples for students.";

const exampleValues = ["4", "9", "16", "25", "2", "72", "-72", "-96"];

const perfectSquareRows = [
  ["1", "1", "1 × 1 = 1"],
  ["4", "2", "2 × 2 = 4"],
  ["9", "3", "3 × 3 = 9"],
  ["16", "4", "4 × 4 = 16"],
  ["25", "5", "5 × 5 = 25"],
  ["36", "6", "6 × 6 = 36"],
  ["49", "7", "7 × 7 = 49"],
  ["64", "8", "8 × 8 = 64"],
  ["81", "9", "9 × 9 = 81"],
  ["100", "10", "10 × 10 = 100"],
];

const squareRootReferenceRows = [
  ["√121", "11", "Perfect square"],
  ["√144", "12", "Perfect square"],
  ["√169", "13", "Perfect square"],
  ["√196", "14", "Perfect square"],
  ["√225", "15", "Perfect square"],
  ["√256", "16", "Perfect square"],
  ["√289", "17", "Perfect square"],
  ["√324", "18", "Perfect square"],
  ["√361", "19", "Perfect square"],
  ["√400", "20", "Perfect square"],
];

const estimationRows = [
  ["√2", "Between 1 and 2", "1² = 1 and 2² = 4"],
  ["√10", "Between 3 and 4", "3² = 9 and 4² = 16"],
  ["√20", "Between 4 and 5", "4² = 16 and 5² = 25"],
  ["√50", "Between 7 and 8", "7² = 49 and 8² = 64"],
  ["√90", "Between 9 and 10", "9² = 81 and 10² = 100"],
];

const commonExampleRows = [
  ["√1", "1", "Perfect square"],
  ["√4", "2", "Perfect square"],
  ["√9", "3", "Perfect square"],
  ["√16", "4", "Perfect square"],
  ["√25", "5", "Perfect square"],
  ["√36", "6", "Perfect square"],
  ["√49", "7", "Perfect square"],
  ["√64", "8", "Perfect square"],
  ["√81", "9", "Perfect square"],
  ["√100", "10", "Perfect square"],
];

const nonPerfectExampleRows = [
  ["√2", "1.414", "Decimal approximation"],
  ["√3", "1.732", "Decimal approximation"],
  ["√5", "2.236", "Decimal approximation"],
  ["√10", "3.162", "Decimal approximation"],
  ["√50", "7.071", "Decimal approximation"],
  ["√99", "9.950", "Decimal approximation"],
];

const relatedTools: ToolLink[] = [
  {
    title: "Square Root Calculator",
    path: "/tools/square-root-calculator",
    description:
      "Open the Square Root Calculator page for quick square root practice and explanation.",
  },
  {
    title: "Percentage Calculator",
    path: "/tools/percentage-calculator",
    description:
      "Open the Percentage Calculator page to calculate percentages and weighted grade values.",
  },
  {
    title: "Scientific Calculator",
    path: "/tools/scientific-calculator",
    description:
      "Open the Scientific Calculator page for broader math and science calculations.",
  },
  {
    title: "Fraction Calculator",
    path: "/tools/fraction-calculator",
    description:
      "Open the Fraction Calculator page to simplify and compare fraction operations.",
  },
  {
    title: "Quadratic Formula Calculator",
    path: "/tools/quadratic-formula-calculator",
    description:
      "Open the Quadratic Formula Calculator page to solve quadratic equations step by step.",
  },
];

const faqs = [
  {
    question: "What is a racine carrée calculator?",
    answer:
      "A racine carrée calculator is an online square root calculator that finds the square root of a number and helps students understand the result.",
  },
  {
    question: "What does racine carrée mean?",
    answer:
      "Racine carrée is the French term for square root. It means the value that gives the original number when multiplied by itself.",
  },
  {
    question: "How do I calculate a square root?",
    answer:
      "To calculate a square root, find the number that multiplies by itself to give the original number. For example, √64 = 8 because 8 × 8 = 64.",
  },
  {
    question: "Can this calculator find decimal square roots?",
    answer:
      "Yes. Enter a decimal such as 2.25 or 0.49 and the calculator will show the square root with a simple explanation.",
  },
  {
    question: "Can I calculate the square root of a negative number?",
    answer:
      "Yes. Negative numbers do not have real square roots, but this calculator can show imaginary square roots using i, such as √-1 = i.",
  },
  {
    question: "Is this racine carrée calculator free?",
    answer:
      "Yes. The SubjectBuddy racine carrée calculator is free to use for square root checks, homework practice, and quick math revision.",
  },
];

function normalizeInput(value: string) {
  return value.trim().replace(",", ".");
}

function parseInput(value: string) {
  const normalizedValue = normalizeInput(value);

  if (!normalizedValue) {
    return { error: "Please enter a number." };
  }

  const parsedValue = Number(normalizedValue);

  if (!Number.isFinite(parsedValue)) {
    return { error: "Please enter a valid number." };
  }

  return { parsedValue, normalizedValue };
}

function formatNumber(value: number, maxDecimals = 6) {
  if (Number.isInteger(value)) {
    return value.toString();
  }

  return Number(value.toFixed(maxDecimals)).toString();
}

function getSimplifiedRadical(value: number) {
  if (!Number.isInteger(value) || value <= 0) {
    return null;
  }

  const root = Math.sqrt(value);
  if (Number.isInteger(root)) {
    return {
      outside: root,
      inside: 1,
    };
  }

  for (let factor = Math.floor(root); factor >= 2; factor -= 1) {
    const square = factor * factor;
    if (value % square === 0) {
      return {
        outside: factor,
        inside: value / square,
      };
    }
  }

  return {
    outside: 1,
    inside: value,
  };
}

function formatRadical(
  radical: { outside: number; inside: number },
  imaginary = false,
) {
  const outsidePart =
    radical.outside === 1 && radical.inside !== 1 ? "" : `${radical.outside}`;
  const insidePart = radical.inside === 1 ? "" : `√${radical.inside}`;
  const base = `${outsidePart}${insidePart}` || "0";

  return imaginary ? `${base}i` : base;
}

function calculateSquareRoot(
  inputValue: string,
  mode: CalculatorMode,
): { error?: string; result?: CalculationResult } {
  const parsed = parseInput(inputValue);

  if ("error" in parsed) {
    return { error: parsed.error };
  }

  const { parsedValue, normalizedValue } = parsed;
  const squareValue = formatNumber(parsedValue * parsedValue);

  if (parsedValue === 0) {
    return {
      result: {
        inputNumber: parsedValue,
        normalizedInput: normalizedValue,
        mode,
        squareValue,
        squareRootResult: "0",
        explanation: "The square root of 0 is 0 because 0 × 0 = 0.",
        isPerfectSquare: true,
        hasImaginaryRoot: false,
      },
    };
  }

  if (parsedValue > 0) {
    const squareRootValue = Math.sqrt(parsedValue);
    const approximateResult = formatNumber(squareRootValue);
    const isPerfectSquare =
      Number.isInteger(parsedValue) && Number.isInteger(squareRootValue);
    const simplifiedRadical = getSimplifiedRadical(parsedValue);

    if (isPerfectSquare) {
      return {
        result: {
          inputNumber: parsedValue,
          normalizedInput: normalizedValue,
          mode,
          squareValue,
          squareRootResult: formatNumber(squareRootValue),
          explanation: `This is a perfect square. The racine carrée of ${formatNumber(parsedValue)} is ${formatNumber(squareRootValue)} because ${formatNumber(squareRootValue)} × ${formatNumber(squareRootValue)} = ${formatNumber(parsedValue)}.`,
          isPerfectSquare: true,
          hasImaginaryRoot: false,
          simplifiedRadical: formatNumber(squareRootValue),
        },
      };
    }

    if (
      simplifiedRadical &&
      simplifiedRadical.inside !== 1 &&
      Number.isInteger(parsedValue)
    ) {
      const radicalText = formatRadical(simplifiedRadical);
      return {
        result: {
          inputNumber: parsedValue,
          normalizedInput: normalizedValue,
          mode,
          squareValue,
          squareRootResult: radicalText,
          approximateResult,
          explanation: `${formatNumber(parsedValue)} is not a perfect square, so its square root is irrational. It can be simplified to ${radicalText} and approximated as ${approximateResult}.`,
          isPerfectSquare: false,
          hasImaginaryRoot: false,
          simplifiedRadical: radicalText,
        },
      };
    }

    return {
      result: {
        inputNumber: parsedValue,
        normalizedInput: normalizedValue,
        mode,
        squareValue,
        squareRootResult: approximateResult,
        explanation: `The square root of ${formatNumber(parsedValue)} is ${approximateResult}. Squaring ${approximateResult} gives ${formatNumber(parsedValue)}.`,
        isPerfectSquare: false,
        hasImaginaryRoot: false,
      },
    };
  }

  const absoluteValue = Math.abs(parsedValue);
  const absoluteRoot = Math.sqrt(absoluteValue);
  const approximateResult = `${formatNumber(absoluteRoot)}i`;
  const simplifiedRadical = getSimplifiedRadical(absoluteValue);
  const simplifiedText = simplifiedRadical
    ? formatRadical(simplifiedRadical, true)
    : approximateResult;

  return {
    result: {
      inputNumber: parsedValue,
      normalizedInput: normalizedValue,
      mode,
      squareValue,
      squareRootResult: simplifiedText,
      approximateResult,
      explanation: `Negative numbers do not have real square roots, but they have imaginary square roots. The square root of ${formatNumber(parsedValue)} is ${simplifiedText}${simplifiedText !== approximateResult ? `, which is approximately ${approximateResult}` : ""}.`,
      isPerfectSquare: false,
      hasImaginaryRoot: true,
      simplifiedRadical: simplifiedText,
    },
  };
}

function RacineCarreeCalculatorPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<CalculatorMode>("square-root");
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<CalculationResult | null>(null);

  function runCalculation(nextValue = inputValue, nextMode = mode) {
    const calculation = calculateSquareRoot(nextValue, nextMode);

    if (calculation.error) {
      setError(calculation.error);
      setResult(null);
      return;
    }

    setError("");
    setResult(calculation.result || null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    runCalculation();
  }

  function handleExampleClick(exampleValue: string) {
    setInputValue(exampleValue);
    runCalculation(exampleValue, mode);
  }

  function handleModeChange(value: string) {
    const nextMode = value as CalculatorMode;
    setMode(nextMode);

    if (inputValue.trim()) {
      runCalculation(inputValue, nextMode);
    }
  }

  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Racine Carrée Calculator",
    url: "https://www.subjectbuddy.com/tools/racine-carree-calculator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "Web",
    description:
      "A free online racine carrée calculator for calculating square roots and learning square root examples.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    provider: {
      "@type": "Organization",
      name: "SubjectBuddy",
      url: "https://www.subjectbuddy.com/",
    },
  };

  return (
    <Container fluid className="racine-page" px={0}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={TOOL_PATH}
        ogTitle="Racine Carrée Calculator"
        ogDescription="Calculate square roots, simplify radicals, check perfect squares, and learn racine carrée steps with this free student calculator."
        keywords={[
          "racine carree calculator",
          "calculatrice racine carrée",
          "racine carrée en ligne",
          "calcul racine carrée",
          "calculer la racine carrée",
          "calculer une racine carrée",
          "simplifier racine carrée",
          "comment calculer une racine carrée",
          "square root calculator",
          "square and square root calculator",
          "square root of -72",
          "square root of -96",
          "root square",
        ]}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: "Racine Carrée Calculator", path: TOOL_PATH },
          ]),
          createFaqSchema(faqs),
          softwareApplicationSchema,
        ]}
      />

      <Box className="racine-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>Racine Carrée Calculator</Text>
            </Breadcrumbs>

            <Button
              color="orange"
              variant="subtle"
              leftSection={<IconArrowLeftBar />}
              onClick={() => navigate(TOOLS_PATH)}
              className="racine-back-button"
            >
              Back to Tools
            </Button>

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={36}>
              <Stack gap="lg" justify="center">
                <Group gap="sm">
                  <Badge color="orange" size="lg" radius="sm">
                    Math Tool
                  </Badge>
                  <Badge color="blue" size="lg" radius="sm" variant="light">
                    Free Student Tool
                  </Badge>
                </Group>

                <Title order={1} className="racine-title">
                  Racine Carrée Calculator
                </Title>

                <Text size="lg" c="dimmed" className="racine-hero-copy">
                  The Racine Carrée Calculator is a free online square root
                  calculator that helps students calculate square roots
                  instantly. Racine carrée means square root in French, and
                  students can use this tool to check perfect squares, decimal
                  square roots, non-perfect square roots, and common math
                  problems.
                </Text>

                <Text c="dimmed" className="racine-intro-note">
                  Racine carrée means square root. For example, the racine
                  carrée of 25 is 5 because 5 × 5 = 25.
                </Text>

                <Alert
                  icon={<IconInfoCircle size={18} />}
                  color="orange"
                  radius="lg"
                  variant="light"
                >
                  This calculator supports positive numbers, decimals, zero,
                  and imaginary square roots for negative numbers.
                </Alert>
              </Stack>

              <section aria-labelledby="racine-calculator-heading">
                <Paper
                  withBorder
                  radius="xl"
                  p="xl"
                  className="racine-tool-card"
                >
                  <form onSubmit={handleSubmit} noValidate>
                    <Stack gap="lg">
                      <Stack gap={4}>
                        <Title id="racine-calculator-heading" order={2}>
                          Calculate a Square Root
                        </Title>
                        <Text c="dimmed">
                          Use it as a square root calculator or switch to the
                          square and square root calculator mode to compare both
                          values together.
                        </Text>
                      </Stack>

                      <SegmentedControl
                        fullWidth
                        value={mode}
                        onChange={handleModeChange}
                        data={[
                          {
                            label: "Square Root Calculator",
                            value: "square-root",
                          },
                          {
                            label: "Square and Square Root Calculator",
                            value: "square-and-root",
                          },
                        ]}
                        aria-label="Calculator mode"
                      />

                      <Stack gap="sm">
                        <Text component="label" htmlFor="racine-number" fw={800}>
                          Enter a number
                        </Text>
                        <TextInput
                          id="racine-number"
                          value={inputValue}
                          onChange={(event) => {
                            setInputValue(event.currentTarget.value);
                            setError("");
                          }}
                          placeholder="Example: 25, 2.25, -72"
                          aria-label="Enter a number"
                          error={error}
                        />
                      </Stack>

                      <Stack gap="xs">
                        <Text size="sm" fw={700} c="dimmed">
                          Try an example
                        </Text>
                        <Group gap="sm">
                          {exampleValues.map((exampleValue) => (
                            <Button
                              key={exampleValue}
                              type="button"
                              variant="light"
                              color="orange"
                              radius="xl"
                              size="compact-md"
                              onClick={() => handleExampleClick(exampleValue)}
                            >
                              {`√${exampleValue}`}
                            </Button>
                          ))}
                        </Group>
                      </Stack>

                      <Button
                        type="submit"
                        size="lg"
                        radius="lg"
                        color="orange"
                        leftSection={<IconCalculator />}
                      >
                        Calculate Square Root
                      </Button>

                      {result ? (
                        <Paper
                          withBorder
                          radius="xl"
                          p="lg"
                          className="racine-result-card"
                          aria-live="polite"
                        >
                          <Stack gap="lg">
                            <Group justify="space-between" align="center">
                              <Stack gap={2}>
                                <Text size="sm" c="dimmed" fw={800} tt="uppercase">
                                  Result
                                </Text>
                                <Title order={3}>
                                  Square Root Result: {result.squareRootResult}
                                </Title>
                              </Stack>
                              <ThemeIcon size={58} radius="xl" color="orange">
                                <IconTargetArrow size={30} />
                              </ThemeIcon>
                            </Group>

                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                              <Paper
                                radius="lg"
                                p="md"
                                className="racine-mini-stat"
                              >
                                <Text fw={900} size="xl">
                                  {result.normalizedInput}
                                </Text>
                                <Text size="sm" c="dimmed">
                                  Entered number
                                </Text>
                              </Paper>
                              <Paper
                                radius="lg"
                                p="md"
                                className="racine-mini-stat"
                              >
                                <Text fw={900} size="xl">
                                  {result.squareRootResult}
                                </Text>
                                <Text size="sm" c="dimmed">
                                  Square root result
                                </Text>
                              </Paper>
                            </SimpleGrid>

                            {mode === "square-and-root" ? (
                              <Paper
                                withBorder
                                radius="lg"
                                p="md"
                                className="racine-square-panel"
                              >
                                <Group justify="space-between">
                                  <Stack gap={2}>
                                    <Text fw={800}>Square of the number</Text>
                                    <Text size="sm" c="dimmed">
                                      number²
                                    </Text>
                                  </Stack>
                                  <Text fw={900} size="xl">
                                    {result.squareValue}
                                  </Text>
                                </Group>
                              </Paper>
                            ) : null}

                            <Divider />

                            <Stack gap="sm">
                              <Group gap="sm">
                                <ThemeIcon color="blue" variant="light">
                                  <IconMathSymbols size={20} />
                                </ThemeIcon>
                                <Text fw={800}>Simplified explanation</Text>
                              </Group>
                              <Text c="dimmed">{result.explanation}</Text>
                              {result.isPerfectSquare ? (
                                <Text fw={700} c="green">
                                  This is a perfect square.
                                </Text>
                              ) : null}
                              {result.approximateResult &&
                              result.approximateResult !== result.squareRootResult ? (
                                <Text fw={700}>
                                  Decimal approximation: {result.approximateResult}
                                </Text>
                              ) : null}
                              {result.hasImaginaryRoot ? (
                                <Text size="sm" c="dimmed">
                                  Negative numbers do not have real square roots,
                                  but they have imaginary square roots.
                                </Text>
                              ) : null}
                            </Stack>
                          </Stack>
                        </Paper>
                      ) : null}
                    </Stack>
                  </form>
                </Paper>
              </section>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <div className="racine-layout">
          <main className="racine-main">
            <article className="racine-content">
              <SeoSection title="What Is a Racine Carrée Calculator?">
                <Text>
                  A racine carrée calculator is a tool that helps you calculate
                  the square root of a number. In French, racine carrée means
                  square root. For example, the racine carrée of 36 is 6
                  because 6 multiplied by 6 equals 36.
                </Text>
                <Text>
                  This page works as both a racine carree calculator and a
                  square root calculator for students who need quick math
                  support.
                </Text>
              </SeoSection>

              <SeoSection title="How to Use This Square Root Calculator">
                <Text>
                  To use this square root calculator, enter a number in the
                  input box and click calculate. The tool will show the square
                  root, explain the result, and tell you whether the number is
                  a perfect square.
                </Text>
                <ol className="racine-steps">
                  <li>Enter your number.</li>
                  <li>Click &quot;Calculate Square Root.&quot;</li>
                  <li>Check the square root result.</li>
                  <li>Read the short explanation.</li>
                </ol>
              </SeoSection>

              <SeoSection title="What Does Racine Carrée Mean?">
                <Text>
                  Racine carrée is the French term for square root. The square
                  root of a number is the value that, when multiplied by itself,
                  gives the original number.
                </Text>
                <Text>
                  For example, the racine carrée of 25 is 5 because 5 × 5 = 25.
                  The same idea is written in English as the square root of 25.
                </Text>
              </SeoSection>

              <SeoSection title="Racine Carrée Formula and Symbol">
                <Text>
                  The square root symbol is √. If √x = y, then y × y = x. This
                  means the racine carrée of a number is the value that produces
                  the original number when it is multiplied by itself.
                </Text>
                <Text>
                  For example, √81 = 9 because 9 × 9 = 81. The opposite
                  operation is squaring: 9² = 81.
                </Text>
                <Group gap="md" className="racine-example-pills">
                  <Text>√x = y</Text>
                  <Text>y² = x</Text>
                  <Text>√81 = 9</Text>
                  <Text>9² = 81</Text>
                </Group>
              </SeoSection>

              <SeoSection title="How to Estimate a Square Root">
                <Text>
                  If the number is not a perfect square, estimate the square
                  root by finding the two perfect squares around it. The answer
                  will sit between those two whole numbers.
                </Text>
                <Text>
                  For example, √20 is between 4 and 5 because 4² = 16 and 5² =
                  25. A calculator can then give the decimal approximation.
                </Text>
                <ResponsiveTable
                  headers={["Expression", "Estimate", "Why"]}
                  rows={estimationRows}
                />
              </SeoSection>

              <SeoSection title="Calcul Racine Carrée: What Does It Mean?">
                <Text>
                  Calcul racine carrée means square root calculation. It is the
                  process of finding the number that, when multiplied by itself,
                  gives the original number.
                </Text>
                <Text>
                  The racine carrée of 49 is 7 because 7 × 7 = 49.
                </Text>
              </SeoSection>

              <SeoSection title="Comment Calculer Une Racine Carrée?">
                <Text>
                  To answer comment calculer une racine carrée, start by
                  checking whether the number is a perfect square. A perfect
                  square is a number like 4, 9, 16, 25, 36, 49, or 64. If the
                  number is not a perfect square, you can estimate the square
                  root using decimals or a calculator.
                </Text>
                <Group gap="md" className="racine-example-pills">
                  <Text>√25 = 5</Text>
                  <Text>√36 = 6</Text>
                  <Text>√2 ≈ 1.414</Text>
                  <Text>√10 ≈ 3.162</Text>
                </Group>
              </SeoSection>

              <SeoSection title="Calculer la Racine Carrée: Perfect Squares">
                <Text>
                  Calculer la racine carrée is easy when the number is a
                  perfect square. A perfect square has a whole-number square
                  root.
                </Text>
                <ResponsiveTable
                  headers={["Number", "Square Root", "Explanation"]}
                  rows={perfectSquareRows}
                />
              </SeoSection>

              <SeoSection title="Square Root Reference Table">
                <Text>
                  Students often need quick square root checks for larger
                  perfect squares. This reference table continues the common
                  square roots from 11 to 20.
                </Text>
                <ResponsiveTable
                  headers={["Expression", "Answer", "Type"]}
                  rows={squareRootReferenceRows}
                />
              </SeoSection>

              <SeoSection title="Racine Carrée of Decimals">
                <Text>
                  This calculatrice racine carrée also works with decimals.
                  Decimal square roots follow the same rule: the answer is the
                  value that multiplies by itself to return the decimal number.
                </Text>
                <Group gap="md" className="racine-example-pills">
                  <Text>√0.25 = 0.5</Text>
                  <Text>√0.49 = 0.7</Text>
                  <Text>√2.25 = 1.5</Text>
                  <Text>√6.25 = 2.5</Text>
                </Group>
              </SeoSection>

              <SeoSection title="Square and Square Root Calculator">
                <Text>
                  A square and square root calculator helps you compare a
                  number, its square, and its square root. The square of a
                  number means multiplying the number by itself. The square root
                  is the opposite operation.
                </Text>
                <Group gap="md" className="racine-example-pills">
                  <Text>Square of 5 = 25</Text>
                  <Text>Square root of 25 = 5</Text>
                </Group>
              </SeoSection>

              <SeoSection title="Simplifying Square Roots">
                <Text>
                  Some square roots are not whole numbers but can still be
                  simplified. To simplify a square root, look for the largest
                  perfect square factor inside the number.
                </Text>
                <Text>
                  For example, 72 contains the perfect square 36. That means
                  √72 = √(36 × 2) = 6√2. The calculator shows this simplified
                  radical form when it is available.
                </Text>
                <Group gap="md" className="racine-example-pills">
                  <Text>√8 = 2√2</Text>
                  <Text>√18 = 3√2</Text>
                  <Text>√48 = 4√3</Text>
                  <Text>√72 = 6√2</Text>
                </Group>
              </SeoSection>

              <SeoSection title="Square Root of Negative Numbers">
                <Text>
                  Negative numbers do not have real square roots because
                  multiplying two real numbers with the same sign gives a
                  positive result. However, negative numbers can have imaginary
                  square roots using i, where i² = -1.
                </Text>
                <Text>
                  The square root of -72 is an imaginary number because -72 has
                  no real square root.
                </Text>
                <Text>
                  The square root of -96 can be simplified using imaginary
                  numbers.
                </Text>
                <Group gap="md" className="racine-example-pills">
                  <Text>Square root of -72 = 6√2i ≈ 8.485i</Text>
                  <Text>Square root of -96 = 4√6i ≈ 9.798i</Text>
                </Group>
              </SeoSection>

              <SeoSection title="Common Square Root Examples">
                <ResponsiveTable
                  headers={["Expression", "Answer", "Type"]}
                  rows={commonExampleRows}
                />
              </SeoSection>

              <SeoSection title="Non-Perfect Square Root Examples">
                <Text>
                  Non-perfect square roots do not simplify to whole numbers.
                  The calculator can show decimal approximations so students can
                  check answers quickly.
                </Text>
                <ResponsiveTable
                  headers={["Expression", "Approximate Answer", "Type"]}
                  rows={nonPerfectExampleRows}
                />
              </SeoSection>

              <SeoSection title="Root Square vs Square Root">
                <Text>
                  Some students search for root square, but the correct math
                  term is usually square root. A square root asks which number
                  multiplied by itself gives the original number.
                </Text>
                <Text>
                  The square root of 64 is 8 because 8 × 8 = 64.
                </Text>
              </SeoSection>

              <SeoSection title="When Students Use This Calculator">
                <Text>
                  Students use this racine carrée calculator for homework,
                  algebra, geometry, exam revision, checking answers, perfect
                  squares, decimal square roots, and quick math practice.
                </Text>
              </SeoSection>

              <SeoSection title="Related Math Tools">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  {relatedTools.map((tool) => (
                    <Paper
                      key={tool.path}
                      component={Link}
                      to={tool.path}
                      withBorder
                      radius="lg"
                      p="lg"
                      className="racine-related-card"
                    >
                      <Stack gap="xs">
                        <Text fw={800} c="orange">
                          {tool.title}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {tool.description}
                        </Text>
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
              </SeoSection>

              <SeoSection title="Related Student Support">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <Paper
                    component={Link}
                    to="/services/math-assignment-help"
                    withBorder
                    radius="lg"
                    p="lg"
                    className="racine-related-card"
                  >
                    <Stack gap="xs">
                      <Text fw={800} c="orange">
                        Math Assignment Help
                      </Text>
                      <Text size="sm" c="dimmed">
                        Get help understanding square roots, radicals, and
                        related math assignment steps.
                      </Text>
                    </Stack>
                  </Paper>
                  <Paper
                    component={Link}
                    to="/assignment-help"
                    withBorder
                    radius="lg"
                    p="lg"
                    className="racine-related-card"
                  >
                    <Stack gap="xs">
                      <Text fw={800} c="orange">
                        Assignment Help
                      </Text>
                      <Text size="sm" c="dimmed">
                        Use SubjectBuddy support when a calculation needs to be
                        applied inside coursework or revision practice.
                      </Text>
                    </Stack>
                  </Paper>
                </SimpleGrid>
              </SeoSection>

              <SeoSection title="Need Help With Square Roots or Math Assignments?">
                <Stack gap="md">
                  <Text>
                    Use the free Racine Carrée Calculator to check square root
                    answers quickly. If you need help understanding math
                    problems, formulas, or assignment questions, SubjectBuddy
                    can guide you step by step.
                  </Text>
                  <Button
                    component={Link}
                    to="/services/math-assignment-help"
                    color="orange"
                    radius="md"
                  >
                    Get Math Assignment Help
                  </Button>
                </Stack>
              </SeoSection>

              <SeoSection title="Racine Carrée Calculator FAQs">
                <Stack gap="md">
                  {faqs.map((faq) => (
                    <Paper key={faq.question} withBorder radius="lg" p="lg">
                      <Title order={3}>{faq.question}</Title>
                      <Text c="dimmed">{faq.answer}</Text>
                    </Paper>
                  ))}
                </Stack>
              </SeoSection>
            </article>
          </main>

          <aside className="racine-sidebar" aria-label="Study support form">
            <div className="racine-sidebar-sticky">
              <Stack gap="md">
                <Paper withBorder radius="xl" p="lg">
                  <Stack gap="xs">
                    <Group gap="sm">
                      <ThemeIcon color="orange" variant="light">
                        <IconSparkles size={20} />
                      </ThemeIcon>
                      <Text size="sm" tt="uppercase" fw={800} c="orange">
                        Study support
                      </Text>
                    </Group>
                    <Title order={3}>Need Help With Math Practice?</Title>
                    <Text c="dimmed" size="sm">
                      Share your topic, deadline, or revision goal and we will
                      help you plan the next study step.
                    </Text>
                  </Stack>
                </Paper>
                <Paper withBorder radius="xl" p={0} className="racine-help-form">
                  <GetHelpForm formSize="sm" flatForm />
                </Paper>
              </Stack>
            </div>
          </aside>
        </div>
      </Container>
    </Container>
  );
}

function SeoSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="racine-seo-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function ResponsiveTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <Box className="racine-table-wrap">
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            {headers.map((header) => (
              <Table.Th key={header}>{header}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.join("-")}>
              {row.map((cell) => (
                <Table.Td key={cell}>{cell}</Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

export default RacineCarreeCalculatorPage;
